import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";

const links = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#caravans", label: "Caravans" },
  { href: "#gallery", label: "Gallery" },
  { href: "#reviews", label: "Reviews" },
  { href: "#faqs", label: "FAQs" },
  { href: "#book", label: "Contact" },
];

export default function Navbar({ promo = "" }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // When at top (over dark hero image), use light text.
  // When scrolled (over cream bg), use dark text.
  const textClass = scrolled ? "text-neutral-900" : "text-white";
  const textMuted = scrolled ? "text-neutral-700" : "text-white/85";
  const borderClass = scrolled
    ? "border-neutral-300 hover:border-neutral-500"
    : "border-white/30 hover:border-white/70";
  const dividerClass = scrolled ? "text-neutral-400" : "text-white/50";

  return (
    <header
      data-testid="navbar"
      style={{ top: promo ? 40 : 0 }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[color:var(--bg)]/90 backdrop-blur-xl border-b border-neutral-200"
          : "bg-gradient-to-b from-black/40 via-black/15 to-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#home"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <span className="diamond-divider" />
          <span
            className={`font-display font-semibold tracking-tight text-lg md:text-xl ${textClass}`}
          >
            Deluxe<span className={dividerClass}> / </span>
            Diamond
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm transition-colors tracking-wide ${textMuted} hover:${textClass}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:0450539879"
            data-testid="navbar-call"
            className={`hidden md:inline-flex items-center gap-2 text-sm px-3 py-2 border transition-colors ${textClass} ${borderClass}`}
          >
            <Phone size={14} />
            0450 539 879
          </a>
          <a
            href="#book"
            data-testid="navbar-book-cta"
            className="hidden md:inline-flex btn-primary text-sm"
            style={
              scrolled
                ? undefined
                : {
                    background: "#a37434",
                    borderColor: "#a37434",
                    color: "#fff",
                  }
            }
          >
            Book Now
          </a>
          <button
            data-testid="navbar-menu-toggle"
            className={`lg:hidden p-2 ${textClass}`}
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-neutral-200 bg-[color:var(--bg)]/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className="text-neutral-800 hover:text-neutral-900 text-base"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#book"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-book"
              className="btn-primary justify-center mt-2"
            >
              Book Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
